import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { searchMovies, getPopularMovies } from "@/lib/movieUtils";
import MovieGrid from "@/components/feature/MovieGrid";
import SectionHeader from "@/components/feature/SectionHeader";

export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `搜索“${query}” - 青青草视频`
      : "影视搜索 - 青青草视频";
    return () => {
      document.title = "青青草视频—高清影视资源在线观看与最新影视推荐";
    };
  }, [query]);

  const results = searchMovies(query);
  const popular = getPopularMovies(10);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
          影视搜索
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600">
          输入影视名称、类型或演员关键词，快速找到想看的内容。
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-3 mb-8 max-w-2xl"
      >
        <div className="flex-1 flex items-center bg-background-100 border border-background-200/70 rounded-md pl-4 pr-1 h-12 focus-within:border-primary-500/60 transition-colors">
          <i className="ri-search-line text-foreground-500 text-lg" />
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索影视名称、类型、演员"
            className="flex-1 bg-transparent px-3 text-sm md:text-base text-foreground-900 placeholder:text-foreground-500 outline-none"
          />
        </div>
        <button
          type="submit"
          className="px-6 h-12 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          搜索
        </button>
      </form>

      {query ? (
        <>
          <p className="text-sm text-foreground-600 mb-5">
            关键词 <span className="text-primary-300 font-semibold">“{query}”</span> ，
            共找到 <span className="text-primary-300 font-semibold">{results.length}</span> 个结果
          </p>

          {results.length > 0 ? (
            <MovieGrid
              movies={results}
              showViews
              columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
            />
          ) : (
            <>
              <div className="w-full py-16 flex flex-col items-center justify-center text-center rounded-lg bg-background-100 border border-dashed border-background-300">
                <span className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mb-4">
                  <i className="ri-search-eye-line text-2xl text-foreground-500" />
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  未找到相关影视内容
                </h2>
                <p className="mt-2 text-sm text-foreground-600">
                  换个关键词试试，或看看下面的热门推荐。
                </p>
              </div>

              <div className="mt-12">
                <SectionHeader
                  title="热门影视推荐"
                  subtitle="大家都在看的高人气作品"
                  icon="ri-fire-line"
                />
                <MovieGrid
                  movies={popular}
                  showViews
                  columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
                />
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <SectionHeader
            title="热门影视推荐"
            subtitle="不知道看什么？先从这里逛起"
            icon="ri-fire-line"
          />
          <MovieGrid
            movies={popular}
            showViews
            columns="grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
          />
        </>
      )}
    </div>
  );
}